import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "https://admin.theslaynetwork.tv/api",
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Endpoints that require authentication — token is only sent with these
const AUTH_REQUIRED = ["/login", "/register", "/email-verify", "/forgot-password", "/reset-password", "/social-login", "/logout", "/user", "/relogin", "/like-dislike-event", "/watch-list-event", "/watch-list"];

// URL patterns that also require auth (matched with .includes())
const AUTH_PATTERNS = ["/streaming-link", "/detail"];

// Only attach token to requests that need it
api.interceptors.request.use((config) => {
  const path = config.url || "";
  const needsAuth =
    AUTH_REQUIRED.some((ep) => path === ep || path.startsWith(ep + "/") || path.startsWith(ep + "?")) ||
    AUTH_PATTERNS.some((pattern) => path.includes(pattern));
  if (needsAuth) {
    const token = localStorage.getItem("auth_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Listeners that get notified when auth becomes invalid (e.g. AuthContext)
let onAuthInvalid = null;
export const setOnAuthInvalid = (callback) => {
  onAuthInvalid = callback;
};

// Token refresh state — prevents multiple simultaneous refresh calls
let isRefreshing = false;
let refreshQueue = []; // requests waiting for the new token

const processQueue = (newToken) => {
  refreshQueue.forEach(({ resolve, reject, config }) => {
    if (newToken) {
      config.headers.Authorization = `Bearer ${newToken}`;
      resolve(api(config));
    } else {
      reject(new Error("Token refresh failed"));
    }
  });
  refreshQueue = [];
};

// Handle 401 / 498 (token expired/invalid) — auto-refresh via /relogin
api.interceptors.response.use(
  (response) => {
    // Some poorly designed APIs return 200 OK with an error message instead of correct status
    const body = response.data;
    if (body && typeof body === 'object' && body.message === 'token invalid') {
      const error = new Error("token invalid");
      error.response = { status: 401, data: body };
      error.config = response.config;
      return Promise.reject(error);
    }
    return response;
  },
  async (error) => {
    const status = error.response?.status;
    const originalRequest = error.config;
    const path = originalRequest?.url || "";

    // Only attempt refresh for token-related errors on auth-required endpoints
    if ((status === 401 || status === 498) && !originalRequest._retried) {
      // Don't try to refresh if the failing request IS the refresh call, or login/register
      const skipRefresh = ["/relogin", "/login", "/register", "/email-verify", "/forgot-password", "/reset-password", "/social-login"];
      if (skipRefresh.some((ep) => path === ep)) {
        return Promise.reject(error);
      }

      const currentToken = localStorage.getItem("auth_token");
      if (!currentToken) {
        // No token at all — user is not logged in, just reject
        return Promise.reject(error);
      }

      originalRequest._retried = true;

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject, config: originalRequest });
        });
      }

      isRefreshing = true;

      try {
        console.log("[Auth] Token expired, refreshing via /relogin...");
        const res = await api.get("/relogin");
        const newToken = res.data?.token;

        if (newToken) {
          console.log("[Auth] Token refreshed successfully");
          localStorage.setItem("auth_token", newToken);

          // Retry the original request with the new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          // Process any queued requests
          processQueue(newToken);

          return api(originalRequest);
        } else {
          throw new Error("No token in refresh response");
        }
      } catch (refreshError) {
        console.warn("[Auth] Token refresh failed, logging out");
        processQueue(null);
        localStorage.removeItem("auth_token");
        localStorage.removeItem("user");
        if (onAuthInvalid) onAuthInvalid();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
